import {
  BoxProps,
  Button,
  Flex,
  Grid,
  GridItem,
  ResponsiveValue,
  Text,
} from '@chakra-ui/react'
import React, { useRef } from 'react'
import { useIsInActiveView } from '~/hooks/useIsInActiveView'
import { useKeyPresses } from '~/hooks/useKeyboard'
import { usePlayDTMF } from '~/hooks/usePlayDTMF'
import { Size } from '~/types/ThemeTypes'

import {
  createKeypadStylesContext,
  useKeypadStyles,
} from './componentStyles/Keypad.style'
import { isKeypadButton } from './utils/stringUtils'

const [StylesProvider, useStyles] = createKeypadStylesContext()

interface KeypadButtonProps {
  digit: string
  characters: string
  onClick: (digit: string) => void
  isActive?: boolean
}
const KeypadButton = ({
  digit,
  characters,
  onClick,
  isActive,
}: KeypadButtonProps) => {
  const styles = useStyles()

  const handleClick = () => {
    onClick(digit)
  }

  return (
    <GridItem sx={styles.buttonContainer} onClick={handleClick}>
      <Button isActive={isActive} sx={styles.button}>
        <Text sx={styles.primaryCharacter}>{digit}</Text>
        <Text sx={styles.secondaryCharacters}>{characters}</Text>
      </Button>
    </GridItem>
  )
}

type KeypadProps = {
  onButtonClick: (digit: string) => void
  size?: ResponsiveValue<Size.sm | Size.md | Size.lg>
  leftButton?: JSX.Element
  centerButton?: JSX.Element
  rightButton?: JSX.Element
  muteDTMF?: boolean
} & BoxProps

export const Keypad = ({
  onButtonClick,
  size,
  leftButton,
  centerButton,
  rightButton,
  muteDTMF = false,
  ...rest
}: KeypadProps) => {
  const keypadRef = useRef<HTMLDivElement>(null)
  const isInActiveView = useIsInActiveView(keypadRef.current)
  const playDTMF = usePlayDTMF()
  useKeyPresses({
    filter: (e) => isInActiveView && isKeypadButton(e.key),
    onPress: (e) => {
      e.preventDefault()
      handleDialButtonClick(e.key)
    },
  })

  const activeButtonsRef = useRef<Record<string, boolean>>({
    '1': false,
    '2': false,
    '3': false,
    '4': false,
    '5': false,
    '6': false,
    '7': false,
    '8': false,
    '9': false,
    '*': false,
    '0': false,
    '#': false,
  })

  const styles = useKeypadStyles({
    size,
  })

  const handleDialButtonClick = (key: string) => {
    onButtonClick(key)
    if (!muteDTMF) {
      playDTMF(key)
    }
    activeButtonsRef.current[key] = true

    setTimeout(() => {
      activeButtonsRef.current[key] = false
    }, 100)
  }

  return (
    <Flex
      ref={keypadRef}
      direction="column"
      alignItems="center"
      w="100%"
      {...rest}
    >
      <Grid sx={styles.keypad} w="fit-content" maxW="100%">
        <StylesProvider value={styles}>
          <KeypadButton
            isActive={activeButtonsRef.current['1']}
            digit="1"
            characters="‎"
            onClick={handleDialButtonClick}
          />
          <KeypadButton
            isActive={activeButtonsRef.current['2']}
            digit="2"
            characters="ABC"
            onClick={handleDialButtonClick}
          />
          <KeypadButton
            isActive={activeButtonsRef.current['3']}
            digit="3"
            characters="DEF"
            onClick={handleDialButtonClick}
          />

          <KeypadButton
            isActive={activeButtonsRef.current['4']}
            digit="4"
            characters="GHI"
            onClick={handleDialButtonClick}
          />
          <KeypadButton
            isActive={activeButtonsRef.current['5']}
            digit="5"
            characters="JKL"
            onClick={handleDialButtonClick}
          />
          <KeypadButton
            isActive={activeButtonsRef.current['6']}
            digit="6"
            characters="MNO"
            onClick={handleDialButtonClick}
          />

          <KeypadButton
            isActive={activeButtonsRef.current['7']}
            digit="7"
            characters="PQRS"
            onClick={handleDialButtonClick}
          />
          <KeypadButton
            isActive={activeButtonsRef.current['8']}
            digit="8"
            characters="TUV"
            onClick={handleDialButtonClick}
          />
          <KeypadButton
            isActive={activeButtonsRef.current['9']}
            digit="9"
            characters="WXYZ"
            onClick={handleDialButtonClick}
          />

          <KeypadButton
            isActive={activeButtonsRef.current['*']}
            digit="*"
            characters="‎"
            onClick={handleDialButtonClick}
          />
          <KeypadButton
            isActive={activeButtonsRef.current['0']}
            digit="0"
            characters="+"
            onClick={handleDialButtonClick}
          />
          <KeypadButton
            isActive={activeButtonsRef.current['#']}
            digit="#"
            characters="‎"
            onClick={handleDialButtonClick}
          />

          {leftButton && (
            <GridItem colStart={1} colEnd={1} sx={styles.buttonContainer}>
              {leftButton}
            </GridItem>
          )}
          {centerButton && (
            <GridItem colStart={2} colEnd={2} sx={styles.buttonContainer}>
              {centerButton}
            </GridItem>
          )}
          {rightButton && (
            <GridItem colStart={3} colEnd={3} sx={styles.buttonContainer}>
              {rightButton}
            </GridItem>
          )}
        </StylesProvider>
      </Grid>
    </Flex>
  )
}
